import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Crown, Shield, HelpCircle, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const FAQ_ITEMS = [
  {
    question: "How do I save an opportunity?",
    answer:
      "Open an opportunity and click 'Save to Projects'. You can then find it in 'My Projects' and continue working from there.",
  },
  {
    question: "What does 'Sign out all' do?",
    answer:
      "It closes all active sessions for your account on every device, then asks you to log in again.",
  },
  {
    question: "How can I delete my account?",
    answer:
      "Go to Settings > Security > Delete Account. You will be asked to confirm before permanent deletion.",
  },
  {
    question: "Why is market data sometimes missing?",
    answer:
      "Some opportunities do not have enough reliable verified anchors yet. In that case, the page stays available but detailed benchmarks may be limited.",
  },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [securityLoading, setSecurityLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const isGoogleAuth = user?.auth_provider === "google";
  const normalizedPlan = (user?.plan || "free").toLowerCase();
  const planLabel =
    normalizedPlan === "pro" || normalizedPlan === "full_access"
      ? "Pro"
      : "Free";

  const avatarSrc =
    user?.avatar_url ||
    user?.picture ||
    user?.identities?.find((identity) => identity?.provider === "google")?.identity_data?.avatar_url ||
    user?.identities?.find((identity) => identity?.provider === "google")?.identity_data?.picture ||
    user?.user_metadata?.avatar_url ||
    user?.user_metadata?.picture ||
    user?.identities?.[0]?.identity_data?.avatar_url ||
    user?.identities?.[0]?.identity_data?.picture ||
    `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(
      user?.name || user?.email || "User"
    )}&backgroundType=gradientLinear`;

  const handleUpgrade = () => {
    navigate("/dashboard/settings/billing");
  };

  const handlePasswordAction = async () => {
    if (!user?.email) return;
    if (isGoogleAuth) {
      window.open("https://myaccount.google.com/security", "_blank", "noopener,noreferrer");
      toast.success("Opened Google security settings");
      return;
    }
    setSecurityLoading(true);
    try {
      toast.info("Password reset flow will be available soon.");
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleManageSessions = async () => {
    setSecurityLoading(true);
    try {
      await apiClient.post("/auth/logout-all");
      await logout();
      toast.success("Signed out on all devices. Please sign in again.");
      navigate("/auth", { replace: true });
    } catch (_error) {
      toast.error("Could not close sessions. Try again.");
    } finally {
      setSecurityLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirmOpen) {
      setDeleteConfirmOpen(true);
      return;
    }

    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type "DELETE" to confirm.');
      return;
    }

    setDeleteLoading(true);
    try {
      await apiClient.delete("/auth/account");
      await logout();
      toast.success("Your account has been deleted.");
      navigate("/auth", { replace: true });
    } catch (_error) {
      toast.error("Could not delete account. Please try again.");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteConfirmText("");
    }
  };

  return (
    <div className="px-4 py-5 md:p-8 lg:p-12 max-w-4xl">
      <div className="mb-6 md:mb-8">
        <h1
          className="text-2xl md:text-4xl font-bold tracking-tight text-white mb-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Settings
        </h1>
        <p className="text-zinc-400">Manage your account and preferences</p>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8 mb-5 md:mb-6">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <User className="w-5 h-5 text-zinc-400" />
          <h2
            className="text-lg font-semibold text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Profile
          </h2>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6 mb-6 md:mb-8">
          <Avatar className="w-20 h-20 shrink-0">
            <AvatarImage src={avatarSrc} alt={user?.name} />
            <AvatarFallback className="bg-[#161616] text-white text-2xl">
              {user?.name?.split(" ").map((part) => part[0]).join("").slice(0, 2) || user?.email?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-xl font-semibold text-white">{user?.name || "User"}</h3>
            <p className="text-zinc-500">{user?.email}</p>
            {user?.skill_level && (
              <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium bg-white/[0.03] border border-[#1F1F1F] text-zinc-300 capitalize">
                {user.skill_level}
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="name" className="text-zinc-400 mb-2 block">Name</Label>
            <Input
              id="name"
              value={user?.name || ""}
              className="bg-[#161616] border-[#1F1F1F] rounded-xl text-white"
              disabled
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-zinc-400 mb-2 block">Email</Label>
            <Input
              id="email"
              value={user?.email || ""}
              className="bg-[#161616] border-[#1F1F1F] rounded-xl text-white"
              disabled
              data-testid="profile-email-input"
            />
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8 mb-5 md:mb-6">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <Crown className="w-5 h-5 text-[#7C3AED]" />
          <h2
            className="text-lg font-semibold text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Subscription
          </h2>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#7C3AED]/10 to-[#6D28D9]/10 border border-[#7C3AED]/20">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-semibold text-white">{planLabel} Plan</span>
              {normalizedPlan === "pro" && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#7C3AED] text-black">
                  ACTIVE
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-400">
              {normalizedPlan === "free"
                ? "Limited AI access and basic features"
                : "Unlimited AI access and advanced analytics"}
            </p>
          </div>
          {normalizedPlan === "free" && (
            <Button
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl w-full sm:w-auto shrink-0"
              onClick={handleUpgrade}
              data-testid="upgrade-plan-btn"
            >
              Upgrade to Pro
            </Button>
          )}
          {normalizedPlan !== "free" && (
            <Button
              variant="outline"
              className="bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-xl w-full sm:w-auto shrink-0"
              onClick={() => navigate("/dashboard/settings/billing")}
              data-testid="manage-billing-btn"
            >
              Manage Billing
            </Button>
          )}
        </div>

        {normalizedPlan === "free" && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-white mb-3">Pro Features Include:</h3>
            <ul className="grid md:grid-cols-2 gap-2 text-sm text-zinc-400">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]"></div>
                Unlimited Cofounder access
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]"></div>
                Advanced market analytics
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]"></div>
                Unlimited project saves
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7C3AED]"></div>
                Priority support
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8 mb-5 md:mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Crown className="w-5 h-5 text-[#7C3AED]" />
          <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Billing
          </h2>
        </div>
        <p className="text-sm text-zinc-400 mb-4">Switch between Free and Pro, and choose monthly or annual billing for Pro.</p>
        <Button
          className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold rounded-xl"
          onClick={() => navigate("/dashboard/settings/billing")}
          data-testid="open-billing-page-btn"
        >
          Open Billing
        </Button>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8 mb-5 md:mb-6">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <Shield className="w-5 h-5 text-zinc-400" />
          <h2
            className="text-lg font-semibold text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Security
          </h2>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-white/[0.02]">
            <div className="min-w-0 pr-2">
              <h3 className="font-medium text-white">Password</h3>
              <p className="text-sm text-zinc-500">
                {isGoogleAuth ? "Signed in with Google" : "Use secure reset flow"}
              </p>
            </div>
            <Button
              variant="outline"
              className="bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-lg w-full sm:w-auto shrink-0"
              onClick={handlePasswordAction}
              disabled={securityLoading}
              data-testid="change-password-btn"
            >
              {isGoogleAuth ? "Google Security" : "Change"}
            </Button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-white/[0.02]">
            <div className="min-w-0 pr-2">
              <h3 className="font-medium text-white">Active Sessions</h3>
              <p className="text-sm text-zinc-500">Sign out this account across all devices.</p>
            </div>
            <Button
              variant="outline"
              className="bg-white/5 border-[#1F1F1F] text-white hover:bg-white/10 rounded-lg w-full sm:w-auto shrink-0"
              onClick={handleManageSessions}
              disabled={securityLoading}
              data-testid="manage-sessions-btn"
            >
              {securityLoading ? "Processing..." : "Sign out all"}
            </Button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-red-500/5 border border-red-500/25">
            <div className="min-w-0 pr-2">
              <h3 className="font-medium text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Delete Account
              </h3>
              <p className="text-sm text-zinc-400">
                Permanently remove your account and clear your personal data.
              </p>
            </div>
            <Button
              variant="outline"
              className="bg-red-500/10 border-red-500/40 text-red-200 hover:bg-red-500/20 rounded-lg w-full sm:w-auto shrink-0"
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
              data-testid="delete-account-btn"
            >
              {deleteLoading ? "Deleting..." : deleteConfirmOpen ? "Confirm delete" : "Delete account"}
            </Button>
            {deleteConfirmOpen && (
              <div className="w-full sm:basis-full">
                <Label htmlFor="delete-confirm" className="text-xs text-zinc-400 mb-2 block">
                  Type <span className="text-red-300 font-semibold">DELETE</span> to confirm.
                </Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="delete-confirm"
                    value={deleteConfirmText}
                    onChange={(event) => setDeleteConfirmText(event.target.value)}
                    className="bg-[#161616] border-red-500/40 text-white"
                    placeholder="DELETE"
                    autoComplete="off"
                    data-testid="delete-account-confirm-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-300 hover:text-white hover:bg-white/5"
                    onClick={() => {
                      setDeleteConfirmOpen(false);
                      setDeleteConfirmText("");
                    }}
                    disabled={deleteLoading}
                    data-testid="delete-account-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-[#1F1F1F] rounded-2xl md:rounded-[2rem] p-5 md:p-8">
        <div className="flex items-center gap-3 mb-5 md:mb-6">
          <HelpCircle className="w-5 h-5 text-zinc-400" />
          <h2
            className="text-lg font-semibold text-white"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Help & Support
          </h2>
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-[#1F1F1F] bg-white/[0.02] p-4 md:p-5">
            <p className="text-sm font-semibold text-white mb-1">Contact Support</p>
            <p className="text-sm text-zinc-400 mb-3">Need help with your account or billing?</p>
            <a
              href="mailto:saas.radar.business@gmail.com"
              className="inline-flex items-center rounded-lg bg-white/5 px-3 py-2 text-sm text-zinc-200 hover:bg-white/10"
              data-testid="contact-email-link"
            >
              saas.radar.business@gmail.com
            </a>
          </div>

          <div className="rounded-xl border border-[#1F1F1F] bg-white/[0.02] p-4 md:p-5">
            <p className="text-sm font-semibold text-white mb-3">Frequently Asked Questions</p>
            <div className="space-y-2">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="group rounded-lg border border-[#1F1F1F] bg-[#161616] px-3 py-2">
                  <summary className="cursor-pointer list-none text-sm font-medium text-zinc-200">
                    {item.question}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
